import {
  createRelayControlJournal,
  summarizeRelayControlRecovery,
  type RelayControlJournalStorage,
  type RelayControlRecoverySummary,
} from "@cmmd-forge/client-runtime";
import { useEffect } from "react";
import { create } from "zustand";

const RELAY_CONTROL_JOURNAL_STORAGE_KEY = "forge.relayControlJournal.v1";

function requireBrowserJournalStorage(): Storage {
  if (typeof globalThis.localStorage === "undefined") {
    throw new Error("Forge relay control recovery requires durable browser storage.");
  }
  return globalThis.localStorage;
}

const browserJournalStorage: RelayControlJournalStorage = {
  read: () => requireBrowserJournalStorage().getItem(RELAY_CONTROL_JOURNAL_STORAGE_KEY),
  write: (value) =>
    requireBrowserJournalStorage().setItem(RELAY_CONTROL_JOURNAL_STORAGE_KEY, value),
};

let relayControlJournal: ReturnType<typeof createRelayControlJournal> | null = null;

interface RelayControlRecoveryStoreState {
  readonly byRuntimeId: Readonly<Record<string, RelayControlRecoverySummary>>;
  readonly replace: (byRuntimeId: Readonly<Record<string, RelayControlRecoverySummary>>) => void;
}

const useRelayControlRecoveryStore = create<RelayControlRecoveryStoreState>()((set) => ({
  byRuntimeId: {},
  replace: (byRuntimeId) => set({ byRuntimeId }),
}));

function syncRecoveryStore(journal: ReturnType<typeof createRelayControlJournal>): void {
  journal.subscribe((snapshot) => {
    const runtimeIds = new Set(snapshot.records.map((record) => record.runtimeId));
    useRelayControlRecoveryStore
      .getState()
      .replace(
        Object.fromEntries(
          [...runtimeIds].map((runtimeId) => [
            runtimeId,
            summarizeRelayControlRecovery(snapshot.records, runtimeId),
          ]),
        ),
      );
  });
}

export function getRelayControlJournal() {
  if (relayControlJournal === null) {
    relayControlJournal = createRelayControlJournal({ storage: browserJournalStorage });
    syncRecoveryStore(relayControlJournal);
  }
  return relayControlJournal;
}

export function useRelayControlRecoveryByRuntimeId(): Readonly<
  Record<string, RelayControlRecoverySummary>
> {
  const byRuntimeId = useRelayControlRecoveryStore((state) => state.byRuntimeId);
  useEffect(() => {
    getRelayControlJournal();
  }, []);
  return byRuntimeId;
}

export function acknowledgeRelayControlRecovery(runtimeId: string): void {
  const journal = getRelayControlJournal();
  for (const record of journal.records()) {
    if (
      record.runtimeId === runtimeId &&
      (record.status === "failed" || record.status === "outcome-unknown")
    ) {
      journal.acknowledge(record.runtimeId, record.actorSessionId, record.transportRequestId);
    }
  }
}
